import {
  Icon as BlueprintIcon,
  Body,
  Box,
  Button,
  HStack,
  useBreakpointValue,
} from '@hover/blueprint';
import { iconEdit, iconInspiration, iconUpload } from '@hover/icons';
import * as Sentry from '@sentry/react';
import { curry } from 'ramda';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  categoryToRouteParams,
  useCustomizeMenuForImage,
} from 'src/modules/homeownerApp/components/Customize/menu';
import type { MaterialRegionOrPaint } from 'src/modules/homeownerApp/constants/customizationCategories';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { usePartnerCobranding } from 'src/modules/homeownerApp/hooks/usePartner';
import type { AiDisplayImageFragment } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useEffectOnMount,
  useQueryParam,
  useTranslate,
} from 'src/shared/hooks';
import { Route, generatePath } from 'src/shared/route';
import { buttonPressed } from 'src/shared/telemetry';

import { PeekingCarousel } from '../../PeekingCarousel';

import { EmbedCustomizeMenu } from './EmbedCustomizeMenu';
import { LandingPageImage } from './EmbedLandingPageImage';

type RouteParams = { [key: string]: string | number | boolean | undefined };

export const setSentryEmbedContext = (isEmbed: boolean) => {
  Sentry.setExtra('isEmbed', isEmbed);
};

const createCustomizePathFn = curry(
  (path: string, params: RouteParams, categoryParams: RouteParams) =>
    generatePath(path, { ...params, ...categoryParams }),
);

export const EmbedLandingPage = ({
  originalImage,
  staticDesignedImage,
  embedDisplayImages,
  onLeadSheetToggle,
}: {
  originalImage?: AiDisplayImageFragment;
  staticDesignedImage?: AiDisplayImageFragment;
  embedDisplayImages: AiDisplayImageFragment[];
  onLeadSheetToggle: () => void;
}) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const isCarouselEnabled = useQueryParam('showCarousel') === 'true';
  // Temporary toggle between using the design item passed in, or from the array if carousel is enabled.
  const [designedImage, setDesignedImage] = useState(
    isCarouselEnabled ? embedDisplayImages[0] : staticDesignedImage,
  );
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isBeforeSelected, setIsBeforeSelected] = useState(false);
  const isDesktop = useBreakpointValue({ base: false, tablet: true });
  const [isBeforeLoaded, setIsBeforeLoaded] = useState(false);
  const [isAfterLoaded, setIsAfterLoaded] = useState(false);
  const targetImage = isBeforeSelected ? originalImage : designedImage;
  const cobranding = usePartnerCobranding();
  const history = useHistory();

  useEffectOnMount(() => {
    setSentryEmbedContext(true);
  });

  const allImagesLoaded = isBeforeLoaded && isAfterLoaded;

  const { menu, loading: menuLoading } = useCustomizeMenuForImage(
    t,
    designedImage,
  );

  const { trackingData } = useHomeownerAppContext();

  if (!originalImage || !designedImage || !targetImage) return null;

  const onUploadCarouselElementClick = () => {
    buttonPressed({
      ...trackingData,
      button_text: 'Upload a photo of your home',
      button_location: 'embed-photo-carousel',
      track_id: 'upload-photo-carousel',
    });

    onLeadSheetToggle();
  };

  const getEditPath = (
    aiDisplayImageId: number,
    categoryName?: MaterialRegionOrPaint,
  ) => {
    const generateCustomizePath = createCustomizePathFn(
      Route.HOMEOWNER_APP_CUSTOMIZE.path,
      { aiDisplayImageId },
    );

    if (categoryName === 'paint') {
      return generateCustomizePath(
        categoryToRouteParams({
          type: categoryName,
          region: 'walls',
        }),
      );
    }
    if (categoryName === 'windows') {
      return generateCustomizePath(
        categoryToRouteParams({
          type: 'material',
          region: 'windows',
        }),
      );
    }
    return generateCustomizePath({ category: 'inspire' });
  };

  const handleEditButtonClick = () => {
    buttonPressed({
      ...trackingData,
      button_text: 'Edit',
      track_id: 'sample-edit-photo-carousel',
    });
    history.push({ pathname: getEditPath(designedImage.id, cobranding?.type) });
  };

  const handleMoreIdeasButtonClick = () => {
    buttonPressed({
      ...trackingData,
      button_text: 'More ideas',
      track_id: 'sample-more-ideas-photo-carousel',
    });
    history.push({
      pathname: generatePath(Route.HOMEOWNER_APP_CUSTOMIZE_INSPIRE.path, {
        aiDisplayImageId: designedImage.id,
      }),
    });
  };

  const editButtonText = cobranding?.type
    ? `${t('projects.edit.edit')} ${cobranding?.type}`
    : t('projects.edit.edit');

  const updateRoute = (index: number) => {
    const currentDisplayImage = embedDisplayImages?.[Math.floor(index / 2)];
    setDesignedImage(currentDisplayImage);
    setCarouselIndex(index);

    if (currentDisplayImage) {
      buttonPressed({
        ...trackingData,
        button_text: currentDisplayImage.style || 'Unknown',
        style: currentDisplayImage.style || undefined,
        space_type: currentDisplayImage.spaceType || undefined,
        track_id: 'sample-design-style-ideas',
      });
    }
  };

  const carouselElements: React.ReactElement[] = [];

  embedDisplayImages.forEach(aiDisplayImage => {
    carouselElements.push(
      <HStack
        justifyContent="center"
        // Most constraints for the carousel element size are enforced on the image itself.
        // This is the only constraint for the upper level container.
        key={aiDisplayImage.id}
        maxW={{
          base: 'auto',
          tablet: 'calc(100dvw - 120px)',
        }}
        pb={{ base: 0, tablet: 200 }}
        position="relative"
      >
        <LandingPageImage
          image={originalImage}
          isBefore
          isBeforeSelected={isBeforeSelected}
          isDesktop={isDesktop}
          isLoaded={allImagesLoaded}
          setIsBeforeSelected={setIsBeforeSelected}
          setIsLoaded={setIsBeforeLoaded}
        />
        <LandingPageImage
          image={aiDisplayImage}
          isBeforeSelected={isBeforeSelected}
          isDesktop={isDesktop}
          isLoaded={allImagesLoaded}
          setIsBeforeSelected={setIsBeforeSelected}
          setIsLoaded={setIsAfterLoaded}
        >
          <Box
            bottom={0}
            display={{ base: 'none', tablet: 'flex' }}
            gap="200"
            justifyContent="center"
            pb={400}
            position="absolute"
          >
            <Button
              color="light"
              iconAfter={iconEdit}
              label={t('embed.edit')}
              onClick={handleEditButtonClick}
              variant="glass"
            >
              {editButtonText}
            </Button>
            <Button
              color="light"
              iconAfter={iconInspiration}
              label={t('embed.moreIdeas')}
              onClick={handleMoreIdeasButtonClick}
              variant="glass"
            >
              {t('embed.moreIdeas')}
            </Button>
          </Box>
        </LandingPageImage>
      </HStack>,
    );
    carouselElements.push(
      <Button
        border="1px solid rgba(232, 232, 232, 0.48)"
        borderRadius="large"
        height={1}
        justifyContent="center"
        key={`${aiDisplayImage.id}-upload`}
        maxW={{ base: '245px', tablet: '496px' }}
        onClick={onUploadCarouselElementClick}
        padding={0}
        pb={{ base: 0, tablet: 200 }}
        position="relative"
        w="calc(100dvw - 80px)"
      >
        <Box alignItems="center" flexDir="column">
          <BlueprintIcon color="white" icon={iconUpload} />
          <Body
            color="white"
            fontWeight={700}
            size="small"
            sx={{ 'text-wrap': 'wrap' }}
            textAlign="center"
            width="70%"
          >
            {t('embed.uploadAPhoto')}
          </Body>
        </Box>
      </Button>,
    );
  });

  if (isCarouselEnabled) {
    return (
      <Box flexDirection="column" h={1} justifyContent="space-between">
        <PeekingCarousel
          peekValueOverrides={{ desktop: 50 }}
          setCurrentIndex={updateRoute}
        >
          {carouselElements}
        </PeekingCarousel>
        {menu && (
          <EmbedCustomizeMenu
            display={{ base: 'flex', tablet: 'none' }}
            isLoading={menuLoading}
            menu={menu}
            showMenu={carouselIndex % 2 === 0}
            targetImage={targetImage}
            zIndex={1}
          />
        )}
      </Box>
    );
  }
  return (
    <Box flexDirection="column" h={1} justifyContent="space-between">
      <HStack
        alignItems="start"
        gap="500"
        h={1}
        justifyContent="center"
        mt={300}
        pb={{ base: 0, tablet: 200 }}
        position="relative"
        px={500}
      >
        <LandingPageImage
          image={originalImage}
          isBefore
          isBeforeSelected={isBeforeSelected}
          isDesktop={isDesktop}
          isLoaded={isBeforeLoaded && isAfterLoaded}
          setIsBeforeSelected={setIsBeforeSelected}
          setIsLoaded={setIsBeforeLoaded}
        />
        <LandingPageImage
          image={designedImage}
          isBeforeSelected={isBeforeSelected}
          isDesktop={isDesktop}
          isLoaded={isBeforeLoaded && isAfterLoaded}
          setIsBeforeSelected={setIsBeforeSelected}
          setIsLoaded={setIsAfterLoaded}
        >
          <Box
            bottom={0}
            display={{ base: 'none', tablet: 'flex' }}
            gap="200"
            justifyContent="center"
            pb={400}
            position="absolute"
            width="100%"
          >
            <Button
              color="light"
              iconAfter={iconEdit}
              label={t('embed.edit')}
              onClick={handleEditButtonClick}
              variant="glass"
            >
              {editButtonText}
            </Button>
            <Button
              color="light"
              iconAfter={iconInspiration}
              label={t('embed.moreIdeas')}
              onClick={handleMoreIdeasButtonClick}
              variant="glass"
            >
              {t('embed.moreIdeas')}
            </Button>
          </Box>
        </LandingPageImage>
      </HStack>
      <EmbedCustomizeMenu
        display={{ base: 'flex', tablet: 'none' }}
        isLoading={menuLoading}
        menu={menu}
        targetImage={targetImage}
        zIndex={1}
      />
    </Box>
  );
};
